import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, ListChecks, CalendarCheck, CircleNotch, CheckCircle, XCircle, Timer, Eye } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { StatusBadge } from "../components/StatusBadge";
import { DataTable } from "../components/DataTable";
import { QueueDetailPanel } from "../components/queue/QueueDetailPanel";
import { useQueueData } from "../usePromo";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../platforms";
import { fmtDateTime, fmtRelative } from "../campaignUtils";

const statusOf = (r) => {
  const s = r.post?.status || r.item.status;
  if (s === "pending" || s === "enqueued") return "scheduled";
  if (s === "processing" || s === "publishing") return "publishing";
  return s; // scheduled | published | failed
};
const errorOf = (r) => (r.post?.results || []).find((x) => x.ok === false)?.error || null;
const schedOf = (r) => r.item.planned_publish_at || r.post?.scheduled_at;
const mktLabel = (k) => MARKETPLACES.find((m) => m.key === k)?.label || k;

export default function Queue() {
  const { t } = useTranslation();
  const { rows, events, loading } = useQueueData();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [marketplace, setMarketplace] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [sort, setSort] = useState("soonest");
  const [selected, setSelected] = useState(null);

  // Enrich rows once.
  const enriched = useMemo(() => rows.map((r) => ({ ...r, id: r.item.id, status: statusOf(r), error: errorOf(r), sched: schedOf(r) })), [rows]);

  const counts = useMemo(() => {
    const c = { scheduled: 0, publishing: 0, published: 0, failed: 0 };
    enriched.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    const next = enriched.filter((r) => r.status === "scheduled" && r.sched && new Date(r.sched) >= new Date())
      .sort((a, b) => new Date(a.sched) - new Date(b.sched))[0];
    return { ...c, next: next?.sched };
  }, [enriched]);

  // Global queue position among scheduled items (soonest first).
  const positionById = useMemo(() => {
    const map = {};
    enriched.filter((r) => r.status === "scheduled").sort((a, b) => new Date(a.sched || 0) - new Date(b.sched || 0))
      .forEach((r, i) => { map[r.item.id] = i + 1; });
    return map;
  }, [enriched]);

  const marketplaces = useMemo(() => [...new Set(enriched.map((r) => r.promo.source_platform).filter(Boolean))], [enriched]);
  const platforms = useMemo(() => [...new Set(enriched.map((r) => r.item.platform).filter(Boolean))], [enriched]);
  const campaigns = useMemo(() => {
    const seen = {}; enriched.forEach((r) => { seen[r.promo.id] = r.promo.name; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [enriched]);
  const anyRetry = useMemo(() => enriched.some((r) => r.post?.retry_count != null), [enriched]);
  const anyError = useMemo(() => enriched.some((r) => r.error), [enriched]);

  const filtered = useMemo(() => {
    let list = enriched.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (marketplace !== "all" && r.promo.source_platform !== marketplace) return false;
      if (platform !== "all" && r.item.platform !== platform) return false;
      if (campaign !== "all" && r.promo.id !== campaign) return false;
      if (q) {
        const hay = `${r.item.product_external_id} ${r.promo.name}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    const cmp = {
      soonest: (a, b) => new Date(a.sched || 0) - new Date(b.sched || 0),
      latest: (a, b) => new Date(b.sched || 0) - new Date(a.sched || 0),
      campaign: (a, b) => a.promo.name.localeCompare(b.promo.name),
      status: (a, b) => a.status.localeCompare(b.status),
    }[sort];
    return [...list].sort(cmp);
  }, [enriched, status, marketplace, platform, campaign, q, sort]);

  const openRow = (r) => {
    const postId = r.item.social_post_id;
    const rowEvents = events.filter((e) => {
      const pl = e.payload || {};
      return (postId && pl.post_id === postId) || pl.promotion_id === r.promo.id || (r.promo.social_campaign_id && pl.campaign_id === r.promo.social_campaign_id);
    });
    setSelected({ ...r, events: rowEvents });
  };

  const opt = (vals, allLabel) => [{ value: "all", label: allLabel }, ...vals];

  const cols = [
    { key: "product", label: t("promo.queue.col.product"), render: (r) => <span className="font-medium">{r.item.product_external_id}</span> },
    { key: "campaign", label: t("promo.queue.col.campaign"), render: (r) => (
      <Link to={`/promotions/campaigns/${r.promo.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand">{r.promo.name}</Link>
    ) },
    { key: "marketplace", label: t("promo.queue.col.marketplace"), render: (r) => {
      const mk = MARKETPLACES.find((m) => m.key === r.promo.source_platform);
      return <span className="flex items-center gap-1.5">{mk && <mk.Icon size={13} />}{mk?.label || r.promo.source_platform}</span>;
    } },
    { key: "platform", label: t("promo.queue.col.platform"), render: (r) => {
      const p = PLATFORM_BY_KEY[r.item.platform];
      return <span className="flex items-center gap-1.5">{p && <p.Icon size={13} />}{p?.label || r.item.platform}</span>;
    } },
    { key: "status", label: t("promo.queue.col.status"), render: (r) => <StatusBadge status={r.status} label={t(`promo.queue.status.${r.status}`, r.status)} /> },
    { key: "sched", label: t("promo.queue.col.scheduled"), render: (r) => fmtDateTime(r.sched) },
    { key: "publish", label: t("promo.queue.col.publish"), render: (r) => r.post?.published_at ? fmtDateTime(r.post.published_at) : "—" },
    { key: "position", label: t("promo.queue.col.position"), align: "right", render: (r) => positionById[r.item.id] ? `#${positionById[r.item.id]}` : "—" },
    ...(anyRetry ? [{ key: "retry", label: t("promo.queue.col.retry"), align: "right", render: (r) => r.post?.retry_count ?? "—" }] : []),
    ...(anyError ? [{ key: "error", label: t("promo.queue.col.error"), render: (r) => r.error ? <span className="block max-w-[200px] truncate text-destructive" title={r.error}>{r.error}</span> : "—" }] : []),
    { key: "actions", label: t("promo.queue.col.actions"), render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); openRow(r); }} className="rounded-md border border-border p-1.5 hover:bg-accent" data-testid={`queue-view-${r.item.id}`}><Eye size={14} /></button>
    ) },
  ];

  return (
    <div className="space-y-6" data-testid="queue-page">
      <PromoPageHeader overline={t("promo.nav.queue")} title={t("promo.queue.title")} description={t("promo.queue.subtitle")} />

      {/* Queue overview */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="queue-overview">
        <KpiCard index={0} testId="q-scheduled" label={t("promo.queue.status.scheduled")} value={counts.scheduled} Icon={CalendarCheck} />
        <KpiCard index={1} testId="q-publishing" label={t("promo.queue.status.publishing")} value={counts.publishing} Icon={CircleNotch} />
        <KpiCard index={2} testId="q-published" label={t("promo.queue.status.published")} value={counts.published} Icon={CheckCircle} />
        <KpiCard index={3} testId="q-failed" label={t("promo.queue.status.failed")} value={counts.failed} Icon={XCircle} />
        <KpiCard index={4} testId="q-next" label={t("promo.queue.nextPublication")} value={counts.next ? fmtRelative(counts.next) : "—"} Icon={Timer} hint={counts.next ? fmtDateTime(counts.next) : undefined} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.queue.search")} className="bg-card pl-9" data-testid="queue-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-36"><PromoSelect value={status} onChange={setStatus} testId="queue-filter-status"
            options={opt(["scheduled", "publishing", "published", "failed"].map((s) => ({ value: s, label: t(`promo.queue.status.${s}`) })), t("promo.queue.allStatus"))} /></div>
          <div className="w-40"><PromoSelect value={marketplace} onChange={setMarketplace} testId="queue-filter-marketplace"
            options={opt(marketplaces.map((mk) => ({ value: mk, label: mktLabel(mk) })), t("promo.catalog.allMarketplaces"))} /></div>
          <div className="w-36"><PromoSelect value={platform} onChange={setPlatform} testId="queue-filter-platform"
            options={opt(platforms.map((p) => ({ value: p, label: PLATFORM_BY_KEY[p]?.label || p })), t("promo.queue.allPlatforms"))} /></div>
          <div className="w-44"><PromoSelect value={campaign} onChange={setCampaign} testId="queue-filter-campaign"
            options={opt(campaigns.map((c) => ({ value: c.id, label: c.name })), t("promo.queue.allCampaigns"))} /></div>
          <div className="w-40"><PromoSelect value={sort} onChange={setSort} testId="queue-sort"
            options={[
              { value: "soonest", label: t("promo.queue.sort.soonest") },
              { value: "latest", label: t("promo.queue.sort.latest") },
              { value: "campaign", label: t("promo.queue.sort.campaign") },
              { value: "status", label: t("promo.queue.sort.status") },
            ]} /></div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={6} cols={7} />
      ) : enriched.length === 0 ? (
        <EmptyState Icon={ListChecks} title={t("promo.queue.emptyTitle")} description={t("promo.queue.emptyDesc")}
          action={<Link to="/promotions/campaigns" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="queue-empty-cta">{t("promo.nav.campaigns")}</Link>} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={MagnifyingGlass} title={t("promo.catalog.noMatch")} description={t("promo.catalog.noMatchDesc")} />
      ) : (
        <DataTable columns={cols} rows={filtered} rowKey="id" onRowClick={openRow} testId="queue-table" />
      )}

      <QueueDetailPanel row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
